// client/src/components/layout/Sidebar.tsx
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import logo from "@/assets/Logo.png";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, UserCircle, Settings } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
}

interface SidebarProps {
  navItems: NavItem[];
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  workspaceLabel: string;
  displayName: string;
  initials: string;
  showSettings?: boolean;
}

export function Sidebar({
  navItems,
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose,
  workspaceLabel,
  displayName,
  initials,
  showSettings = false,
}: SidebarProps) {
  const [location] = useLocation();
  const { logout } = useAuth();
  const sidebarContent = (
    <aside
      className={cn(
        "flex h-full flex-col border-r border-slate-200 bg-white transition-[width] duration-200",
        collapsed ? "w-16" : "w-60"
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center border-b border-slate-100 px-3">
        <Link href="/" className="flex items-center">
          {collapsed ? (
            <img src={logo} alt="180Pi" className="h-7 w-auto mx-auto" />
          ) : (
            <img src={logo} alt="180Pi" className="h-8 w-auto" />
          )}
        </Link>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto px-2 py-4">
        <ul className="space-y-0.5">
          {navItems.map((item) => {
            const isActive = location === item.href;
            return (
              <li key={item.href}>
                <Link href={item.href} onClick={onMobileClose}>
                  <span
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      collapsed && "justify-center px-2",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isActive ? "text-primary" : "text-slate-400"
                      )}
                    />
                    {!collapsed && item.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom: language + user + collapse toggle */}
      <div className="border-t border-slate-100 px-2 py-3 space-y-1">
        {!collapsed && <LanguageSwitcher />}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                collapsed && "justify-center px-2"
              )}
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700">
                {initials}
              </div>
              {!collapsed && (
                <div className="min-w-0 text-left">
                  <div className="truncate text-xs font-semibold text-slate-900">{displayName}</div>
                  <div className="truncate text-[10px] text-slate-500">{workspaceLabel}</div>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={8}
            className="w-56 rounded-3xl border border-slate-200 bg-white p-0 shadow-[0px_20px_28px_rgba(0,0,0,0.14)]"
          >
            <DropdownMenuLabel className="px-2.5 pt-2.5">
              <div className="flex items-center gap-2.5 border-b border-stone-300 pb-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] outline outline-2 outline-white">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium leading-4 text-black">{displayName}</p>
                  <p className="truncate text-[11px] font-medium leading-4 text-neutral-400">
                    {workspaceLabel}
                  </p>
                </div>
              </div>
            </DropdownMenuLabel>
            <div className="px-1.5 py-1.5">
              <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-2.5 py-2 text-xs font-medium text-black focus:bg-slate-50 focus:text-black">
                <Link href="/profile" className="flex items-center gap-2.5">
                  <UserCircle className="h-3.5 w-3.5" />
                  <span>{"Profile"}</span>
                </Link>
              </DropdownMenuItem>
              {showSettings && (
                <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-2.5 py-2 text-xs font-medium text-black focus:bg-slate-50 focus:text-black">
                  <Link href="/settings" className="flex items-center gap-2.5">
                    <Settings className="h-3.5 w-3.5" />
                    <span>{"Settings"}</span>
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="my-2 bg-stone-300" />
              <DropdownMenuItem
                onClick={logout}
                className="cursor-pointer rounded-xl px-2.5 py-2 text-xs font-medium text-black focus:bg-slate-50 focus:text-black"
              >
                <div className="flex items-center gap-2.5">
                  <LogOut className="h-3.5 w-3.5" />
                  <span>{"Logout"}</span>
                </div>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Collapse toggle */}
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-center rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar — fixed */}
      <div className="hidden md:fixed md:inset-y-0 md:left-0 md:z-50 md:flex">
        {sidebarContent}
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 md:hidden transition-transform duration-200",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="relative flex h-full w-60 flex-col">
          <button
            className="absolute right-3 top-3 rounded-lg p-1 text-slate-400 hover:bg-slate-100"
            onClick={onMobileClose}
          >
            <X className="h-4 w-4" />
          </button>
          {sidebarContent}
        </div>
      </div>
    </>
  );
}
